import {useAtomValue} from 'jotai';
import {observer, useLocalObservable} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {useLock} from '_/hooks';
import {useMasterdata} from '_/hooks/useMasterdata';
import {I18NextNS} from '_/i18n';
import {
  CreateFromTemplateDialog,
  FillPlaceholders,
  Preview,
} from '_/sharedComponents/CreateFromTemplateWizard';
import {userState} from '_/state/user';
import {TemplateWizardStep} from '_/store/template';
import {
  allowedSearchQueryActions,
  getRouteUrl,
  globalSearchParams,
  State,
} from '_/utilities';

import {CreateDocumentFromTemplateStore} from '../../store';

import ChooseTemplate from './ChooseTemplate';

type Props = {
  className?: string;
  casusId?: string;
  folderId?: string;
  onClose: () => void;
  forReporting?: boolean;
};

const UCreateDocumentFromTemplate = ({
  className,
  casusId,
  folderId,
  onClose,
  forReporting = false,
}: Props): React.ReactElement => {
  const loggedInUser = useAtomValue(userState);
  const location = useLocation();
  const [_, setSearchParams] = useSearchParams();
  const {translatedLocales: locales} = useMasterdata();
  const localStore = useLocalObservable(
    () =>
      new CreateDocumentFromTemplateStore({
        loggedInUser,
        originatingCasusId: casusId,
        originatingFolderId: folderId,
        forReporting,
      })
  );
  const {t} = useTranslation<I18NextNS>('documents');
  const navigate = useNavigate();
  const {safeCallback} = useLock(
    {
      id: 'documents.create-document-from-template',
      message: t('common:forms.discardConfirmation'),
    },
    () => localStore.modified,
    [localStore.modified]
  );

  const stepForward = useCallback(async () => {
    if (localStore.wizardStep === TemplateWizardStep.ChooseTemplate) {
      await localStore.getOverridableTemplatePlaceholders();
    } else if (localStore.wizardStep === TemplateWizardStep.Preview) {
      if (localStore.chooseTemplateData.casusId) {
        const newDocumentId = await localStore.createDocumentFromTemplate();
        const url = getRouteUrl({
          module: 'casus-documents',
          casusId: localStore.chooseTemplateData.casusId,
          folderId: localStore.chooseTemplateData.folderId,
          documentId: newDocumentId,
          action: 'open-document',
          clearFilters: true,
        });
        const isAlreadyThere = url.startsWith(location.pathname);
        if (!newDocumentId) {
          return;
        }

        if (isAlreadyThere) {
          setSearchParams({
            [globalSearchParams.documents.activeDocumentId]: newDocumentId,
            [globalSearchParams.documents.reload]: 'true',
            [globalSearchParams.action]:
              allowedSearchQueryActions.documents.openDocument,
          });
          return;
        }
        navigate(url);
      } else {
        localStore.downloadReport();
      }
    } else {
      await localStore.stepForward();
    }
    // localStore.wizardStep and localStore.chooseTemplateData are necessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localStore,
    localStore.wizardStep,
    localStore.chooseTemplateData,
    localStore.temporaryDocumentId,
    navigate,
  ]);

  useEffect(() => localStore.unsubscribe, [localStore.unsubscribe]);

  const placeholderScreens = useMemo(
    () =>
      localStore.placeholderLoader.data
        ?.map((pagedPlaceholder) => ({
          [pagedPlaceholder.key]: (
            <FillPlaceholders
              key={pagedPlaceholder.key}
              backLabel={t('common:actions.back')}
              casusId={casusId}
              fileName={localStore.chooseTemplateData.fileName}
              isLoading={localStore.placeholderLoader.state === State.Loading}
              nextLabel={t('common:actions.next')}
              pagedPlaceholder={pagedPlaceholder}
              onBackClick={localStore.stepBack}
              onNextClick={stepForward}
              onParentEntityChange={localStore.fetchDependablePlaceholderValues}
            />
          ),
        }))
        .reduce((acc, value) => ({...acc, ...value}), {}),
    [
      casusId,
      localStore.placeholderLoader.data,
      localStore.placeholderLoader.state,
      localStore.chooseTemplateData.fileName,
      localStore.fetchDependablePlaceholderValues,
      localStore.stepBack,
      stepForward,
      t,
    ]
  );

  const isTemporaryDocumentCreating =
    localStore.temporaryDocumentDataState === State.Loading;
  const isTemporaryDocumentReady =
    localStore.temporaryDocumentDataState === State.Success;
  const isReportDownloadable =
    Boolean(forReporting) &&
    isTemporaryDocumentReady &&
    Boolean(localStore.temporaryDocumentId);
  const isDocumentCreationEnabled =
    !forReporting &&
    !isTemporaryDocumentCreating &&
    !localStore.isCreatingDocument;
  const reportDownloadLabel = isTemporaryDocumentCreating
    ? t('reporting.isBeingCreated')
    : t('common:actions.download');

  const wizardScreens = useMemo(
    () => ({
      [TemplateWizardStep.ChooseTemplate]: locales.length ? (
        <ChooseTemplate
          backLabel={t('common:actions.cancel')}
          forReporting={forReporting}
          localStore={localStore}
          nextLabel={t('common:actions.next')}
          onBackClick={safeCallback(onClose)}
          onNextClick={stepForward}
        />
      ) : null,
      ...placeholderScreens,
      [TemplateWizardStep.Preview]: (
        <Preview
          backLabel={t('common:actions.back')}
          documentId={localStore.temporaryDocumentId}
          documentState={localStore.temporaryDocumentDataState}
          documentTitle={localStore.chooseTemplateData.fileName}
          errorText={t('reporting.creationFailed')}
          isSubmitting={isTemporaryDocumentCreating}
          nextButtonDisabled={
            !isReportDownloadable && !isDocumentCreationEnabled
          }
          nextLabel={
            forReporting ? reportDownloadLabel : t('common:actions.create')
          }
          onBackClick={localStore.stepBack}
          onNextClick={stepForward}
        />
      ),
    }),
    // The dependency temporaryDocumentDataState
    // is necessary for the changes to be recognized
    [
      locales.length,
      t,
      forReporting,
      localStore,
      safeCallback,
      onClose,
      stepForward,
      placeholderScreens,
      isTemporaryDocumentCreating,
      isReportDownloadable,
      isDocumentCreationEnabled,
      reportDownloadLabel,
    ]
  );

  return (
    <CreateFromTemplateDialog
      className={className}
      dialogHeader={
        forReporting
          ? t('create.fromTemplate.dialog.reportHeader')
          : t('create.fromTemplate.dialog.header')
      }
      steps={localStore.wizardSteps}
      wizardScreens={wizardScreens}
      wizardStep={localStore.wizardStep}
      onClose={safeCallback(onClose)}
    />
  );
};

export default observer(UCreateDocumentFromTemplate);
